import { useReducer, useMemo, useCallback } from 'react';

/**
 * Modello di stato esplicito per le SELEZIONI dell'utente nel flusso batch.
 * Volutamente separato dallo stato asincrono (recenti, piano, polling, risultati),
 * che vive in hook dedicati. Un solo reducer, azioni tipizzate, nessun grappolo
 * di useState scollegati.
 */

export const initialSelectionState = {
  step: 'source',
  productId: '',
  moduleIds: [], // number[]
  macroFunctionIds: [], // number[] — macrofunzioni selezionate
  skeletons: [], // { root, path }[]
  logLevel: 'INFO',
  // Default del nuovo frontend: genera solo ciò che non è aggiornato.
  regenerationMode: 'OUTDATED_ONLY', // 'OUTDATED_ONLY' | 'ALL'
  filters: {
    mine: false,
    timePreset: 'all', // 'all' | '7d' | '30d' | '90d' | 'custom'
    since: '', // input date (YYYY-MM-DD) quando timePreset === 'custom'
    until: '',
    search: '', // ricerca testuale lato client
    genStatus: 'all', // 'all' | 'to_generate' | 'generated' (client-side)
  },
};

const skeletonKey = (s) => `${s.root}::${s.path}`;

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step };

    case 'SET_PRODUCT':
      // Moduli e macrofunzioni dipendono dal prodotto: non conservare una
      // selezione invisibile appartenente al prodotto precedente.
      return { ...state, productId: action.productId, moduleIds: [], macroFunctionIds: [] };

    case 'TOGGLE_MODULE': {
      const exists = state.moduleIds.includes(action.moduleId);
      return {
        ...state,
        moduleIds: exists
          ? state.moduleIds.filter((id) => id !== action.moduleId)
          : [...state.moduleIds, action.moduleId],
        macroFunctionIds: [],
      };
    }

    case 'SET_MODULES':
      return { ...state, moduleIds: action.moduleIds, macroFunctionIds: [] };

    case 'TOGGLE_MACRO': {
      const exists = state.macroFunctionIds.includes(action.id);
      return {
        ...state,
        macroFunctionIds: exists
          ? state.macroFunctionIds.filter((id) => id !== action.id)
          : [...state.macroFunctionIds, action.id],
      };
    }

    case 'SET_MACROS':
      return { ...state, macroFunctionIds: action.ids };

    case 'CLEAR_MACROS':
      return { ...state, macroFunctionIds: [] };

    case 'TOGGLE_SKELETON': {
      const key = skeletonKey(action.skeleton);
      const exists = state.skeletons.some((s) => skeletonKey(s) === key);
      return {
        ...state,
        skeletons: exists
          ? state.skeletons.filter((s) => skeletonKey(s) !== key)
          : [...state.skeletons, action.skeleton],
      };
    }

    case 'CLEAR_SKELETONS':
      return { ...state, skeletons: [] };

    case 'SET_LOG_LEVEL':
      return { ...state, logLevel: action.logLevel };

    case 'SET_REGENERATION_MODE':
      return { ...state, regenerationMode: action.mode };

    case 'SET_FILTER':
      return {
        ...state,
        filters: { ...state.filters, [action.key]: action.value },
      };

    case 'PATCH_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.patch } };

    case 'RESET':
      return initialSelectionState;

    default:
      return state;
  }
}

export function useSelectionState() {
  const [state, dispatch] = useReducer(reducer, initialSelectionState);

  const actions = useMemo(
    () => ({
      setStep: (step) => dispatch({ type: 'SET_STEP', step }),
      setProduct: (productId) => dispatch({ type: 'SET_PRODUCT', productId }),
      toggleModule: (moduleId) => dispatch({ type: 'TOGGLE_MODULE', moduleId }),
      setModules: (moduleIds) => dispatch({ type: 'SET_MODULES', moduleIds }),
      toggleMacro: (id) => dispatch({ type: 'TOGGLE_MACRO', id }),
      setMacros: (ids) => dispatch({ type: 'SET_MACROS', ids }),
      clearMacros: () => dispatch({ type: 'CLEAR_MACROS' }),
      toggleSkeleton: (skeleton) => dispatch({ type: 'TOGGLE_SKELETON', skeleton }),
      clearSkeletons: () => dispatch({ type: 'CLEAR_SKELETONS' }),
      setLogLevel: (logLevel) => dispatch({ type: 'SET_LOG_LEVEL', logLevel }),
      setRegenerationMode: (mode) => dispatch({ type: 'SET_REGENERATION_MODE', mode }),
      setFilter: (key, value) => dispatch({ type: 'SET_FILTER', key, value }),
      patchFilters: (patch) => dispatch({ type: 'PATCH_FILTERS', patch }),
      reset: () => dispatch({ type: 'RESET' }),
    }),
    [],
  );

  /** Verifica se una macrofunzione è selezionata. */
  const isMacroSelected = useCallback(
    (id) => state.macroFunctionIds.includes(id),
    [state.macroFunctionIds],
  );

  /** Verifica se uno scheletro è selezionato. */
  const isSkeletonSelected = useCallback(
    (skeleton) =>
      state.skeletons.some((s) => skeletonKey(s) === skeletonKey(skeleton)),
    [state.skeletons],
  );

  return { state, actions, isMacroSelected, isSkeletonSelected };
}
